/**
 * Build a structured digest of prior site reports for the Executive Summary
 * report type. The LLM uses this to synthesize a concise BTM data-center
 * viability brief without re-running domain analyses.
 */
import { getReportType, type ReportTypeId } from '../config/reportTypes';
import type { ExecutiveSummaryPreferences, SiteRagReport } from '../types/siteRag';
import {
  financialWithheldReasonLabel,
  resolveFinancialDisplayVerdict,
} from './reportVerdict';

export interface ExecutiveSummarySourceEntry {
  reportId: string;
  reportType: ReportTypeId;
  typeLabel: string;
  title: string;
  generatedAt?: string;
  verdictTier?: string;
  /**
   * D15 — the source's BANDED VERDICT LABEL, resolved through the ONE display
   * verdict (`resolveFinancialDisplayVerdict` for financial reports).
   *
   * `verdictTier` above carries `report.overallStatus`, a raw `RagStatus`
   * ('green' / 'amber' / 'red') with no vocabulary and no cap disclosure — and
   * for a financial report with a WITHHELD band it carries whatever generation
   * happened to leave there. The Decision Guide was therefore summarising the
   * LLM's prose ABOUT the model plus a colour word, never the model's own
   * verdict. Absent when the report type has no banded verdict.
   */
  verdictLabel?: string;
  /**
   * D15 — the deterministic headline metrics behind the verdict, pre-formatted
   * for the digest. Financial reports only (from `financialAnalysis.headline`);
   * empty for every other type. Gives the synthesis the NUMBERS, so it can
   * summarise the MODEL rather than a narrative about it.
   */
  headlineMetricLines: string[];
  executiveSummary?: string;
  keyFindingLines: string[];
  conclusion?: string;
}

const SYNTHESIS_SOURCE_TYPES: ReadonlySet<ReportTypeId> = new Set([
  'feasibility',
  'environmental',
  'test-fit',
  'power-availability',
  'water-resource',
  'interconnection',
  'public-sentiment',
  'custom-brief',
  // FIN-1 (wf w7nuyquwd) — Financial Viability Report is an eligible
  // Decision-Guide source: its deterministic verdict + headline metrics feed
  // the Go/No-Go rollup worst-source floor and the exec-summary synthesis.
  'financial',
  // Supply Chain Risk was the one generated type absent from this set, despite
  // `docs/architecture/exec-summary-authoritative-design.md` §52 explicitly
  // instructing that the source set be EXTENDED to include it. The omission
  // meant a generated, intent-relevant procurement diligence never reached the
  // Decision Guide narrative even while it drove the 0.08 supplyChain pillar.
  'supply-chain-risk',
]);

export function isEligibleExecutiveSummarySource(report: SiteRagReport): boolean {
  if (!report.reportType) return false;
  if (report.reportType === 'executive-summary') return false;
  return SYNTHESIS_SOURCE_TYPES.has(report.reportType);
}

function legacyGeneratedAt(report: SiteRagReport): string | undefined {
  const value = (report as { generatedAt?: unknown }).generatedAt;
  return typeof value === 'string' ? value : undefined;
}

function legacyRevision(report: SiteRagReport): number | undefined {
  const value = (report as { revision?: unknown }).revision;
  return typeof value === 'number' ? value : undefined;
}

function legacyVerdictTier(report: SiteRagReport): string | undefined {
  const value = (report as { verdictTier?: unknown }).verdictTier;
  return typeof value === 'string' ? value : undefined;
}

function reportTimestamp(report: SiteRagReport): number {
  const date = legacyGeneratedAt(report) ?? report.createdAt;
  return date ? new Date(date).getTime() : 0;
}

/** One newest eligible source report per report type (for exec-summary picker). */
export function latestEligibleExecutiveSummarySources(reports: SiteRagReport[]): SiteRagReport[] {
  const byType = new Map<ReportTypeId, SiteRagReport>();
  for (const report of reports) {
    if (!isEligibleExecutiveSummarySource(report)) continue;
    const type = report.reportType!;
    const existing = byType.get(type);
    if (!existing || reportTimestamp(report) > reportTimestamp(existing)) {
      byType.set(type, report);
    }
  }
  return [...byType.values()].sort((a, b) => {
    const labelA = getReportType(a.reportType!).shortLabel;
    const labelB = getReportType(b.reportType!).shortLabel;
    return labelA.localeCompare(labelB);
  });
}

/**
 * #25 (Decision Guide as a LIVING doc) — default Executive-Summary
 * preferences that auto-select ALL latest eligible completed source reports
 * for a site.
 *
 * The Executive Summary report IS the "Decision Guide": it auto-aggregates
 * every completed domain report into one GO / CONDITIONAL / NO-GO call. The
 * wizard previously landed the user on an empty source picker (a manual
 * generate step) which made the guide feel un-live. Seeding ALL completed
 * sources up-front turns the flow into a one-click "always current" guide —
 * the user opens it and the latest of every domain report is already in scope.
 *
 * `existingFocus` carries any synthesisFocus the seed already resolved
 * (parent / sibling / site default) so re-running the guide preserves the
 * user's framing notes while still refreshing the source set to the newest
 * reports. Returns `undefined` when there are NO eligible sources so the
 * caller can fall through to the empty-source affordance rather than seed an
 * empty (invalid) preference object.
 */
export function defaultExecutiveSummarySourcePrefs(
  reports: SiteRagReport[],
  existingFocus?: string,
): ExecutiveSummaryPreferences | undefined {
  const eligible = latestEligibleExecutiveSummarySources(reports);
  if (eligible.length === 0) return undefined;
  return {
    sourceReportIds: eligible.map((r) => r.id),
    ...(existingFocus?.trim() ? { synthesisFocus: existingFocus.trim() } : {}),
  };
}

function reportTitle(report: SiteRagReport): string {
  const typeLabel = report.reportType ? getReportType(report.reportType).shortLabel : 'Report';
  const revNumber = report.version ?? legacyRevision(report);
  const rev = revNumber ? ` v${revNumber}` : '';
  return `${typeLabel}${rev}`;
}

function trimStringField(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function extractConclusionText(report: SiteRagReport): string | undefined {
  const raw = report.conclusion;
  if (typeof raw === 'string') return trimStringField(raw);
  if (raw && typeof raw === 'object' && typeof raw.recommendation === 'string') {
    return trimStringField(raw.recommendation);
  }
  return undefined;
}

function extractKeyFindingLines(report: SiteRagReport): string[] {
  const lines: string[] = [];
  const structured = report.structuredFindings ?? [];
  for (const f of structured.slice(0, 8)) {
    const text = [f.title, f.detail].filter(Boolean).join(' — ').trim();
    if (text) lines.push(text);
  }
  if (lines.length === 0 && Array.isArray(report.contextualKeyFindings)) {
    for (const k of report.contextualKeyFindings.slice(0, 8)) {
      if (typeof k?.title === 'string' && k.title.trim()) lines.push(k.title.trim());
      if (typeof k?.detail === 'string' && k.detail.trim()) lines.push(k.detail.trim());
    }
  }
  return lines;
}

const USD = (v: number): string => `$${Math.round(v).toLocaleString('en-US')}`;
const isNum = (v: number | null | undefined): v is number =>
  typeof v === 'number' && Number.isFinite(v);

/**
 * D15 — the DETERMINISTIC headline of a financial source, as digest lines.
 *
 * These are the engine's own numbers (`financialAnalysis.headline`), not the
 * narrative's restatement of them, so the Decision Guide synthesises the MODEL.
 * The hurdle line names its own provenance for the same reason
 * `reportPresentation.resolveHurdleProvenance` does: on most reports there is
 * no target return and the "hurdle" IS the WACC, which makes the IRR test a
 * restatement of the NPV test rather than a second, independent gate.
 */
function financialHeadlineLines(report: SiteRagReport): string[] {
  const stamp = report.financialAnalysis;
  if (!stamp) return [];
  const h = stamp.headline;
  const lines: string[] = [];
  if (isNum(h.npvUsd)) lines.push(`NPV: ${USD(h.npvUsd)}`);
  if (isNum(h.irrPct)) {
    const hurdle = isNum(h.hurdleRatePct)
      ? ` vs a ${h.hurdleRatePct.toFixed(1)}% ${isNum(h.waccPct) && Math.abs(h.hurdleRatePct - h.waccPct) > 1e-9 ? 'target return (override)' : 'cost of capital (WACC)'}`
      : '';
    lines.push(`Project IRR (unlevered): ${h.irrPct.toFixed(1)}%${hurdle}`);
  }
  if (isNum(h.lcoeUsdPerMwh)) lines.push(`LCOE: ${USD(h.lcoeUsdPerMwh)}/MWh`);
  if (isNum(h.paybackYears)) {
    const life = isNum(h.projectLifeYears) ? ` of a ${Math.round(h.projectLifeYears)}-year life` : '';
    lines.push(`Payback: ${h.paybackYears.toFixed(1)} years${life}`);
  }
  if (isNum(h.capexPerKwUsd)) lines.push(`CAPEX: ${USD(h.capexPerKwUsd)}/kW`);
  if (isNum(h.annualMwh)) lines.push(`Annual energy: ${Math.round(h.annualMwh).toLocaleString('en-US')} MWh`);
  return lines;
}

/**
 * D15 — the source's banded verdict LABEL for the digest.
 *
 * For a financial report this is the ONE display verdict, so the Decision Guide
 * reads the same verdict as the report's own cover/body — including the cap
 * disclosure when a cap moved the tier off the model's band, and an explicit
 * WITHHELD (with its reason) rather than a silent absence that the synthesis
 * would otherwise be free to read as "fine".
 */
function verdictLabelForDigest(report: SiteRagReport): string | undefined {
  if (report.reportType !== 'financial') return undefined;
  if (!report.financialAnalysis) return undefined;
  const fv = resolveFinancialDisplayVerdict(report);
  if (fv.withheld) {
    return `Verdict withheld — ${financialWithheldReasonLabel(fv.withheldReason)}`;
  }
  return fv.capped
    ? `${fv.label} (model band ${fv.modelLabel}, capped: ${fv.caps.map((c) => c.name).join(', ') || 'evidence/feasibility limit'})`
    : fv.label;
}

export function buildExecutiveSummarySourceDigest(
  sourceReports: SiteRagReport[],
): { digestText: string; entries: ExecutiveSummarySourceEntry[] } {
  const entries: ExecutiveSummarySourceEntry[] = sourceReports.map((report) => {
    const reportType = report.reportType!;
    const verdictLabel = verdictLabelForDigest(report);
    return {
      reportId: report.id,
      reportType,
      typeLabel: getReportType(reportType).label,
      title: reportTitle(report),
      generatedAt: legacyGeneratedAt(report) ?? report.createdAt,
      verdictTier: report.overallStatus ?? legacyVerdictTier(report),
      ...(verdictLabel ? { verdictLabel } : {}),
      headlineMetricLines: financialHeadlineLines(report),
      executiveSummary: trimStringField(report.executiveSummary) ?? trimStringField(report.summary),
      keyFindingLines: extractKeyFindingLines(report),
      conclusion: extractConclusionText(report),
    };
  });

  const blocks = entries.map((e, idx) => {
    const parts: string[] = [
      `--- SOURCE ${idx + 1}: ${e.typeLabel} (${e.reportId}) ---`,
    ];
    // The banded verdict comes FIRST and before the prose: it is the
    // deterministic call, and the narrative below is a description of it.
    if (e.verdictLabel) parts.push(`Deterministic verdict: ${e.verdictLabel}`);
    if (e.verdictTier) parts.push(`Verdict tier: ${e.verdictTier}`);
    if (e.headlineMetricLines.length > 0) {
      parts.push('Deterministic headline metrics (engine-computed — use these figures verbatim):');
      e.headlineMetricLines.forEach((line) => parts.push(`  - ${line}`));
    }
    if (e.executiveSummary) parts.push(`Executive summary:\n${e.executiveSummary}`);
    if (e.keyFindingLines.length > 0) {
      parts.push('Key findings:');
      e.keyFindingLines.forEach((line, i) => parts.push(`  ${i + 1}. ${line}`));
    }
    if (e.conclusion) parts.push(`Conclusion:\n${e.conclusion}`);
    return parts.join('\n');
  });

  const digestText = blocks.length > 0
    ? [
      'PRIOR SITE REPORTS — synthesize these into one concise BTM data-center viability brief.',
      'Datacrate mission: determine whether behind-the-meter (BTM) energy can viably power a data center at this site.',
      '',
      ...blocks,
    ].join('\n\n')
    : '';

  return { digestText, entries };
}
